/**
 * This is the base widget mixin that should eventually be applied to ALL widgets.
 * Currently, this only adds some accessibility features so it has not been applied globally.
 * Any widget that needs strong ARIA support(is used in workspaces) or strong Focus support, should have this mixin applied at some point.
 */
export default function mixin(baseClass) {

    //Will be added to events in child widget.
    var eventsMixin = [
        // triggered when the widget element is focused
        "focused",
        // triggered when the widget element is blurred
        "blurred"
    ];

    /**
     * Will be called after init in child widget.
     */
    var initMixin = function() {
        this.initFocusable();
    };

    /**
     * Will be called after destroy in child widget.
     */
    var destroyMixin = function() {
        this.destroyFocusable(this._focusableFocus, this._focusableBlur);
        this.cleanupScreenReaderDescriptions();
    };

    var originalExtend = baseClass.extend;

    var resultClass = baseClass.extend({
        /**
         * Get the elements that are potentially directly focusable and must have ARIA attributes applied.
         */
        getARIAElements: function() {
            if (this.wrapper && this.wrapper.attr("role")) {
                return this.wrapper;
            } else {
                return this.element;
            }
        },

        _setARIAAttr: function(category, id, mapName, attrName) {
            const backup = `data-${mapName}-original`;
            if (!this[mapName]) {
                this[mapName] = new Map();
                this.getARIAElements().each((i, el) => {
                    const $el = $(el);
                    $(el).attr(backup, $el.attr(attrName));
                })
            }
            this[mapName].set(category, id);
            const stored = Array.from(this[mapName].values())
            this.getARIAElements().attr(attrName, function(){
                return [$(this).attr(backup), ...stored]
                    .filter(v => !!v) //only bother with values that actually exist
                    .flatMap(val => val.split(" ")) // break apart any values that contain multiple
                    .filter((val, index, me) => {
                        //remove duplicates keeping the first occurrence
                        return me.indexOf(val) === index;
                    })
                    .join(" ")
            });
        },

        /**
         * Set a described by to the appropriate location for this widget.
         * @param category Unique category so that describedby can properly replace "old" versions.
         * @param describedById ID of described by widget.
         */
        setDescribedBy: function(category, describedById) {
            this._setARIAAttr(category, describedById, "_describedByApplies", "aria-describedby");
        },

        /**
         * Set a labelledby to the appropriate location for this widget.
         * @param category
         * @param labelledById
         */
        setLabelledBy: function(category, labelledById) {
            this._setARIAAttr(category, labelledById, "_labelledByApplies", "aria-labelledby");
        },

        /**
         * Add an explicit description that is only read by screen readers.
         * @param category Category of text. Allows Text to be removed.
         * @param text Text to add
         */
        setScreenReaderDescription: function(category, text) {
            if (!this._screenReaderDescriptions) {
                this._screenReaderDescriptions = new Map();
            }
            if (!this._screenReaderDescriptions.has(category)) {
                var id = kendo.guid();
                var desc = $("<span>")
                    .hide()
                    .attr("id", id)
                    .appendTo(document.body);
                this.setDescribedBy(category, id);
                this._screenReaderDescriptions.set(category, desc);
            }
            var el = this._screenReaderDescriptions.get(category);
            el.text(text);
        },

        removeScreenReaderDescription: function(category) {
            if (this._screenReaderDescriptions && this._screenReaderDescriptions.has(category)) {
                this._screenReaderDescriptions.get(category).remove();
                this._screenReaderDescriptions.delete(category);
                this.setDescribedBy(category, "");
            }
        },

        cleanupScreenReaderDescriptions: function() {
            if (this._screenReaderDescriptions) {
                this._screenReaderDescriptions.forEach(function(el) {
                    el.remove();
                });
                delete this._screenReaderDescriptions;
            }
        },

        _initFocusableProxied: false,
        initFocusable: function() {
            if(this._initFocusableProxied === false) {
                this._focusableFocus = this._focusableFocus.bind(this);
                this._focusableBlur = this._focusableBlur.bind(this);
                this._initFocusableProxied = true;
            }
            this.initFocusableEvents(this._focusableFocus, this._focusableBlur);
        },

        initFocusableEvents: function(onFocus, onBlur) {
            this.getARIAElements()
                .on("focus", onFocus)
                .on("blur", onBlur);
        },

        destroyFocusable: function(onFocus, onBlur) {
            this.getARIAElements()
                .off("focus", onFocus)
                .off("blur", onBlur);
        },

        /**
         * Focus handler for the widget element that triggers a "focused" event
         * @private
         */
        _focusableFocus: function() {
            this.trigger("focused");
        },

        /**
         * Blur handler for the widget element that triggers a "blurred" event
         * @private
         */
        _focusableBlur: function() {
            this.trigger("blurred");
        }
    });

    /**
     * This does some horrible things to allow events/init/destroy to be properly overridden.
     * They can't be normal mixins, because then children would have to know how to call them instead of "normal" parent class.
     * This on the fly patches the provided options to include the mixins.  The downside to this is that it can't be stopped by the child.
     * All init/destroy logic should be actually in overridable functions in case it needs changed by a child.
     * @param extendWith
     */
    resultClass.extend = function(extendWith) {
        var init = (extendWith.init || this.fn.init);
        var destroy = (extendWith.destroy || this.fn.destroy);
        var e = new Set();
        (extendWith.events || []).concat(this.fn.events).concat(eventsMixin).forEach(function(ev) {
            e.add(ev);
        });
        return originalExtend.call(this, $.extend({}, extendWith, {
            events: Array.from(e),
            init: function() {
                init.apply(this, arguments);
                if (!this._initMixinDone) {
                    this._initMixinDone = true;
                    initMixin.call(this);
                }
            },
            destroy: function() {
                if (!this._destroyMixinDone) {
                    this._destroyMixinDone = true;
                    destroyMixin.call(this);
                }
                destroy.apply(this, arguments);
            }
        }));
    };

    return resultClass;
};
